import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { PhoneIcon, ArrowRightIcon } from '@heroicons/react/24/solid';
import { TvIcon } from '@heroicons/react/20/solid';
import { useAutoAnimate } from '@formkit/auto-animate/react';
import autoAnimate from '@formkit/auto-animate';
import { Navigate, useNavigate } from 'react-router-dom';
import {
    InputGroup,
    InputGroupAddon,
    InputGroupButton,
    InputGroupInput,
    InputGroupText,
    InputGroupTextarea,
} from "@/components/ui/input-group"
import { CheckIcon, CircleAlert, Moon, Phone, RotateCcw, SquareAsterisk, Sun } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import {
    InputOTP,
    InputOTPGroup,
    InputOTPSeparator,
    InputOTPSlot,
} from "@/components/ui/input-otp"
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import UseDarkMode from '@/Elements/UseDarkMode';
import ConfirmationDialog from '@/Elements/ConfirmationDialog';
import { useAuth } from '@/Services/AuthContext';
import Arc from '@/lib/Arc';
import { Bounce, toast } from 'react-toastify';
const LoginScreen = () => {

    const [step, setStep] = useState(1);
    const [phone, setPhone] = useState('');
    const [guardian, setGuardian] = useState(null);

    const phoneValidationSchema = Yup.object().shape({
        phone: Yup.string()
            .matches(/^[0-9]{10}$/, 'Phone number must be 10 digits')
            .required('Phone number is required'),
    });

    const { user, isAuthenticated, loading, login, logout } = useAuth();

    useEffect(() => {
        if (isAuthenticated)
            navigate('/home');
    }, [isAuthenticated])

    const handlePhoneSubmit = (values, { setSubmitting }) => {
        setPhone(values.phone);
        console.log('Initiating OTP login for:', values.phone);
        // toast("Wow so easy!")
        Arc().serviceCall('guardian', 'initiateOTPLogin', [
            values.phone
        ]).then(res => {
            console.log('OTP initiated successfully:', res);
            // Store the token for OTP verification
            // localStorage.setItem('otp_token', res.result);
            toast.success('OTP sent successfully!', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "colored",
                transition: Bounce,
            });
            setStep(2);
            setSubmitting(false);
            setGuardian(res.result);
        }).catch(error => {
            // console.error('OTP initiation failed:', error);
            toast.error('Oops! Something went wront while requesting OTP, please try again.', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "colored",
                transition: Bounce,
            });
            // You might want to show an error message to the user here
            setSubmitting(false);
        });
    };

    const [otp, setOtp] = useState('');
    const [countdown, setCountdown] = useState(60);
    const [isRetryEnabled, setIsRetryEnabled] = useState(false);
    const navigate = useNavigate();

    const otpValidationSchema = Yup.object().shape({
        otp: Yup.string()
            .matches(/^[0-9]{4}$/, 'OTP must be 4 digits')
            .required('OTP is required'),
        full_name: Yup.string().when([], {
            is: () => guardian?.new_user, // Access new_user directly from the guardian state
            then: (schema) => schema.required('Full Name is required'),
            otherwise: (schema) => schema.notRequired(),
        }),
    });

    const parent = useRef(null)
    useEffect(() => {
        parent.current && autoAnimate(parent.current, {
            // Animation duration in milliseconds (default: 250)
            duration: 500,
            // Easing for motion (default: 'ease-in-out')
            easing: 'ease-in-out',
            // When true, this will enable animations even if the user has indicated
            // they don’t want them via prefers-reduced-motion.
            disrespectUserMotionPreference: false,
        })
    }, [parent])

    useEffect(() => {
        let timer;
        if (step === 2 && countdown > 0) {
            timer = setTimeout(() => {
                setCountdown(prev => prev - 1);
            }, 1000);
        } else if (countdown === 0) {
            setIsRetryEnabled(true);
        }
        return () => clearTimeout(timer);
    }, [step, countdown]);

    const handleOtpSubmit = (values, { setSubmitting, setErrors }) => {
        Arc().serviceCall('guardian', 'confirmOTPLogin', [
            phone,
            guardian.otp_token || '', // You'll need to store the token from initiateOTPLogin response
            values.otp,
            values.full_name
        ]).then(res => {
            console.log('OTP Verified successfully:', res);
            login(res.result);
            navigate('/home');
        }).catch(error => {
            console.error('OTP verification failed:', error);
            setErrors({ otp: 'Invalid OTP' });
            setSubmitting(false);
        });
    };

    const handleResendOtp = () => {
        setCountdown(60);
        setIsRetryEnabled(false);
        // Here you would typically dispatch a new OTP
        console.log('Resending OTP');
    };

    const [isDarkMode, toggleDarkMode] = UseDarkMode();

    return (
        <section className='w-screen h-screen flex relative'>
            <Button onClick={() => { toggleDarkMode(!isDarkMode) }} className="absolute top-5 right-5" variant="ghost" size="icon" aria-label="Submit">
                {isDarkMode ? (<Moon />) : (<Sun />)}
            </Button>
            {/* <div className='hidden md:block h-full w-2/3 relative dark:bg-white/10 bg-primary/40'>
            </div> */}
            <div className='h-full w-full flex flex-col items-center justify-center px-6'>
                <motion.img
                    src="/logo_sm.png"
                    alt="Logo"
                    initial={{ scale: 0, y: -50 }}
                    animate={{
                        scale: 1,
                        y: 0
                    }}
                    transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 10,
                        mass: 1,
                        bounce: 0.2
                    }}
                    className="h-32 w-38"
                />
                {step === 1 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{
                            delay: 0.6,
                        }}
                        className="w-full max-w-sm"
                    >
                        <h3 className='text-2xl mt-6 dark:text-slate-50 capitalize text-center'>
                            Sign in with your phone number
                        </h3>
                        {/* {console.log(phone)} */}
                        <Formik
                            initialValues={{ phone: phone }}
                            validationSchema={phoneValidationSchema}
                            onSubmit={handlePhoneSubmit}
                        >
                            {({ errors, touched, isSubmitting, handleChange }) => (
                                <Form className="space-y-4" >
                                    <div ref={parent}>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-50 mb-1 mt-3">
                                            Phone Number
                                        </label>

                                        <InputGroup className="mt-2">
                                            <InputGroupInput defaultValue={phone} name="phone" onChange={handleChange} placeholder="Your Mobile Number" autoFocus className={'tracking-widest'} />
                                            <InputGroupAddon>
                                                +91
                                            </InputGroupAddon>
                                            {/* <InputGroupAddon align="inline-end">12 results</InputGroupAddon> */}

                                        </InputGroup>



                                        {(errors.phone && touched.phone) ? (
                                            <div className="text-red-500 text-sm mt-1">{errors.phone}</div>
                                        ) : (<div className="text-sm">

                                        </div>)}

                                        <p className='text-sm dark:text-slate-50 mt-1'>
                                            Use the phone number which you have used at ToonTopia.
                                        </p>

                                        <Button disabled={isSubmitting} className="w-full mt-4">
                                            Lets Go <ArrowRightIcon className="h-4 w-4" />
                                            {isSubmitting && <Spinner />}
                                        </Button>

                                        {/* <Button style={{ width: "100%" }} variant="classic" className='mt-16' loading={isSubmitting}>
                                            Lets Go <ArrowRightIcon className="h-4 w-4" />
                                        </Button> */}
                                    </div>


                                </Form>
                            )}
                        </Formik>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        className="w-full max-w-sm">
                        <div className=" ">
                            <h3 className='text-2xl mt-6 dark:text-slate-50 capitalize text-center'>
                                Verify your phone number
                            </h3>
                            <Formik
                                initialValues={{ otp: '', full_name: guardian?.full_name === 'unnamed web user' ? '' : guardian?.full_name || '' }}
                                validationSchema={otpValidationSchema}
                                onSubmit={handleOtpSubmit}
                            >
                                {({ errors, touched, isSubmitting, handleChange }) => (
                                    <Form className="">
                                        {guardian?.new_user && (
                                            <>
                                                <label className="block text-sm font-medium text-gray-700 dark:text-slate-50 mb-1 mt-3">
                                                    Your Full Name
                                                </label>
                                                <InputGroup className="mt-2">
                                                    <InputGroupInput
                                                        name="full_name"
                                                        onChange={handleChange}
                                                        placeholder="Your Full Name"
                                                        autoFocus
                                                    />
                                                    <InputGroupAddon>
                                                        <TvIcon className="h-4 w-4" />
                                                    </InputGroupAddon>
                                                </InputGroup>
                                                {(errors.full_name && touched.full_name) ? (
                                                    <div className="text-red-500 text-sm mt-1">{errors.full_name}</div>
                                                ) : (<div className="text-sm">

                                                </div>)}
                                            </>
                                        )}
                                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-50 mt-5">
                                            Your One Time Password
                                        </label>
                                        <div className="flex justify-center space-x-2">
                                            <InputGroup className="mt-2">
                                                <InputGroupInput
                                                    name="otp"
                                                    onChange={handleChange}
                                                    placeholder="4 Digit One Time Password"
                                                    autoFocus={!guardian?.new_user}
                                                    maxLength="4"
                                                />
                                                <InputGroupAddon>
                                                    <SquareAsterisk />
                                                </InputGroupAddon>
                                            </InputGroup>
                                        </div>
                                        {(errors.otp && touched.otp) ? (
                                            <div className="text-red-500 text-sm mt-1">{errors.otp}</div>
                                        ) : (<div className="text-sm">

                                        </div>)}
                                        <p className="text-sm text-gray-500 mt-1">
                                            We've sent a code to <u className='text-primary'>{phone}</u>
                                        </p>

                                        <Button type="submit" disabled={isSubmitting} className="w-full mt-5">
                                            Verify OTP <CheckIcon className="h-4 w-4" />
                                            {isSubmitting && <Spinner />}
                                        </Button>
                                        <div className='py-3 flex justify-between items-center'>
                                            <ConfirmationDialog
                                                title="Are you sure?"
                                                message='Cancelling now will discard the currently dispatched one time password, confirm and go back?'
                                                onConfirm={() => setStep(1)}
                                                // open={true}
                                                trigger={(<Button variant="ghost" className="">
                                                    Cancel and Go Back
                                                </Button>)}
                                            ></ConfirmationDialog>
                                            <div className="flex items-center space-x-2">
                                                <span className="text-sm dark:text-slate-50">{countdown > 0 ? `Retry in ${countdown}s` : 'Ready to retry'}</span>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button variant="ghost" size="icon" onClick={handleResendOtp} disabled={!isRetryEnabled}>
                                                            <RotateCcw />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent asChild>
                                                        <span className='text-white bg-secondary '>
                                                            Retry again.
                                                        </span>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </div>
                                        <div className='w-full bg-primary/50 rounded' style={{ height: "1px" }}></div>
                                    </Form>
                                )}
                            </Formik>
                        </div>
                    </motion.div>
                )}

            </div>
        </section>
    );
};

export default LoginScreen;